import { Suspense, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useMatches } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import ManageAccountsOutlined from '@mui/icons-material/ManageAccountsOutlined';
import SearchRounded from '@mui/icons-material/SearchRounded';
import SpaceDashboardOutlined from '@mui/icons-material/SpaceDashboardOutlined';
import { SparkField } from '../../design/SparkField';
import { PageFade } from '../../design/motion';
import { Kicker, Wordmark, SparkMark } from '../../design/surfaces';
import { color, cssEase, font, glass, monoLabel, radius } from '../../design/tokens';
import { login, logout, type User } from '../../services/auth.service';
import { useAuth } from '../../services/useAuth';
import { CookieBanner } from '../CookieBanner/CookieBanner';
import { ShellChromeProvider, useShellChromeValue } from './ShellChrome';
import { SEARCH_SHORTCUT_LABEL, SearchDialog, useSearchShortcut } from './SearchDialog';
import { initials } from '../../utils/format';
import { ImpersonationBanner } from './ImpersonationBanner';
import dayjs from 'dayjs';

/**
 * Route `handle` flags read by the shell.
 * - immersive: page fills the viewport below the top bar and manages its own scrolling (boards, retros)
 */
export interface ShellHandle {
    immersive?: boolean;
    /**
     * Paths of this route that are one page (its tabs, e.g. /w/:slug/:tab?): moving between them keeps the page,
     * without its entrance animation. The page is told apart by this name and the route's other params.
     */
    page?: string;
}

/** What makes a different page for the entrance animation: the path, or a `page` route's name and params. */
export function pageKeyFor(pathname: string, matches: Array<{ handle: unknown; params: Record<string, string | undefined> }>): string {
    const match = [...matches].reverse().find(m => (m.handle as ShellHandle | undefined)?.page);
    if (!match) return pathname;
    const { tab: _tab, ...params } = match.params;
    return `${(match.handle as ShellHandle).page}:${JSON.stringify(params)}`;
}

export const TOP_BAR_HEIGHT = 60;

/** "Premium, granted by Persera until Nov 24, 2026" / "…, no end date" for an admin grant. */
export function grantLabel(user: Pick<User, 'premiumGrantExpiresAt'>) {
    return user.premiumGrantExpiresAt
        ? `Premium, granted by Persera until ${dayjs(user.premiumGrantExpiresAt).format('MMM D, YYYY')}`
        : 'Premium, granted by Persera (no end date)';
}

/** "Free plan", "Premium", "Premium · Persera admin"... for menus and the Account page. */
function planLabel(user: User) {
    switch (user.premiumSource) {
        case 'admin': return 'Premium · Persera admin';
        case 'granted': return grantLabel(user);
        case 'subscription': return user.subscription.isTrial ? 'Premium trial' : 'Premium';
        case 'trial': return 'Premium trial';
        default: return 'Free plan';
    }
}

function AccountMenu() {
    const { user, loading } = useAuth();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    if (loading) return <Box sx={{ width: 36 }} />;
    if (!user) {
        return <Button variant="contained" size="small" onClick={() => login()}>Sign in</Button>;
    }

    const name = user.username || user.email;
    return (
        <>
            <IconButton onClick={e => setAnchor(e.currentTarget)} aria-label="Account menu" sx={{ p: 0.5 }}>
                <Avatar sx={{
                    width: 32, height: 32, fontSize: 13, fontFamily: font.display,
                    bgcolor: 'transparent', color: color.text,
                    border: `1px solid ${user.premium ? color.spark : color.lineStrong}`,
                    boxShadow: user.premium ? `0 0 14px -2px ${color.spark}` : 'none',
                }}>
                    {initials(name)}
                </Avatar>
            </IconButton>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 240, mt: 1 } } }}
            >
                <Box sx={{ px: 2, py: 1.25 }}>
                    <Typography sx={{ fontFamily: font.display, fontWeight: 500 }} noWrap>{name}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{user.email}</Typography>
                    <Kicker sx={{ mt: 1 }} tone={user.premium ? color.spark : color.textFaint}>
                        {planLabel(user)}
                    </Kicker>
                </Box>
                <Divider />
                <MenuItem component={RouterLink} to="/" onClick={() => setAnchor(null)}>
                    <ListItemIcon><SpaceDashboardOutlined fontSize="small" /></ListItemIcon>
                    My sparks
                </MenuItem>
                <MenuItem component={RouterLink} to="/account" onClick={() => setAnchor(null)}>
                    <ListItemIcon><ManageAccountsOutlined fontSize="small" /></ListItemIcon>
                    Account &amp; plan
                </MenuItem>
                {user.workspaces.map(w => (
                    <MenuItem key={w.id} component={RouterLink} to={`/w/${w.slug}`} onClick={() => setAnchor(null)}>
                        <ListItemIcon><BusinessOutlined fontSize="small" /></ListItemIcon>
                        {w.name} workspace
                    </MenuItem>
                ))}
                {user.isPerseraAdmin && (
                    <MenuItem component={RouterLink} to="/admin" onClick={() => setAnchor(null)}>
                        <ListItemIcon><AdminPanelSettingsOutlined fontSize="small" /></ListItemIcon>
                        Persera admin
                    </MenuItem>
                )}
                <MenuItem onClick={() => logout()}>
                    <ListItemIcon><LogoutOutlined fontSize="small" /></ListItemIcon>
                    Sign out
                </MenuItem>
            </Menu>
        </>
    );
}

/**
 * Opens search over my spaces and ones shared with me (signed in only). A field-like button on wide
 * screens, an icon on narrow ones or when a space's title and tools fill the bar (`compact`).
 */
function SearchTrigger({ compact }: { compact: boolean }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const show = useCallback(() => setOpen(true), []);
    useSearchShortcut(user ? show : noop);
    if (!user) return null;

    return (
        <>
            {/* Hover only: focus returns here when search closes, and a focus tooltip would linger (on phones especially) */}
            <Tooltip title={`Search (${SEARCH_SHORTCUT_LABEL})`} disableFocusListener disableTouchListener>
                <IconButton aria-label="Search your spaces" onClick={show}
                    sx={{ display: compact ? 'inline-flex' : { xs: 'inline-flex', md: 'none' }, color: color.textMuted }}>
                    <SearchRounded />
                </IconButton>
            </Tooltip>
            {!compact && (
                <ButtonBase onClick={show} aria-label="Search your spaces" sx={{
                    display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, width: 260, height: 36, px: 1.25,
                    borderRadius: `${radius.sm}px`, color: color.textFaint, backgroundColor: 'rgba(5, 4, 15, 0.45)',
                    outline: `1px solid ${color.line}`, outlineOffset: -1, transition: 'outline-color .2s, color .2s',
                    '&:hover': { outlineColor: color.lineStrong, color: color.textMuted },
                }}>
                    <SearchRounded sx={{ fontSize: 18 }} />
                    <Typography component="span" sx={{ flex: 1, textAlign: 'left', fontSize: 14 }}>Search</Typography>
                    <Typography component="kbd" sx={{ fontFamily: font.mono, fontSize: 11, px: 0.75, py: 0.25, borderRadius: '3px', backgroundColor: 'rgba(140,170,255,0.08)' }}>
                        {SEARCH_SHORTCUT_LABEL}
                    </Typography>
                </ButtonBase>
            )}
            <SearchDialog open={open} onClose={() => setOpen(false)} contentSearch={!!user.features?.contentSearch} />
        </>
    );
}

const noop = () => { };

/** More than this many workspaces share one "Workspaces" menu in the top bar instead of a link each. */
const WORKSPACE_LINKS_MAX = 2;

/**
 * The signed-in person's CoSparks Workspaces, in the top bar next to the logo: a link to each one's page (/w/:slug),
 * marked while you're in it. Inside a space, phones leave the room to the space's title.
 */
export function WorkspaceNav({ hasTitle, currentSlug }: { hasTitle: boolean; currentSlug?: string }) {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const workspaces = user?.workspaces ?? [];
    if (!workspaces.length) return null;

    // On its page, or in one of its spaces
    const isHere = (slug: string) => slug === currentSlug || pathname === `/w/${slug}` || pathname.startsWith(`/w/${slug}/`);
    const pill = (here: boolean) => ({
        display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 32, px: 1.25, minWidth: 0,
        borderRadius: `${radius.sm}px`, fontFamily: font.display, fontSize: 14, fontWeight: 500, textDecoration: 'none',
        color: here ? color.text : color.textMuted,
        backgroundColor: here ? 'rgba(140, 170, 255, 0.14)' : 'transparent',
        transition: 'background-color .2s, color .2s',
        '&:hover': { color: color.text, backgroundColor: 'rgba(140, 170, 255, 0.1)' },
        '& svg': { fontSize: 17, flexShrink: 0 },
    });

    return (
        <Box component="nav" aria-label="Workspaces" sx={{ display: hasTitle ? { xs: 'none', md: 'flex' } : 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexShrink: 1 }}>
            {workspaces.length <= WORKSPACE_LINKS_MAX
                ? workspaces.map(w => (
                    <ButtonBase key={w.id} component={RouterLink} to={`/w/${w.slug}`} aria-current={isHere(w.slug) ? 'page' : undefined}
                        aria-label={`${w.name} workspace`} sx={pill(isHere(w.slug))}>
                        <BusinessOutlined />
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: { xs: 120, sm: 200 } }}>{w.name}</Box>
                    </ButtonBase>
                ))
                : (
                    <>
                        <ButtonBase onClick={e => setAnchor(e.currentTarget)} aria-haspopup="menu" sx={pill(workspaces.some(w => isHere(w.slug)))}>
                            <BusinessOutlined />Workspaces
                        </ButtonBase>
                        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}>
                            {workspaces.map(w => (
                                <MenuItem key={w.id} component={RouterLink} to={`/w/${w.slug}`} selected={isHere(w.slug)} onClick={() => setAnchor(null)}>
                                    <ListItemIcon><BusinessOutlined fontSize="small" /></ListItemIcon>
                                    {w.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}
        </Box>
    );
}

export function TopBar() {
    const chrome = useShellChromeValue();
    const hasTitle = !!chrome.title;
    return (
        <Box
            component="header"
            sx={{
                position: 'sticky', top: 0, zIndex: 20,
                height: TOP_BAR_HEIGHT, flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 },
                px: { xs: 1.5, sm: 2.5 },
                ...glass,
                borderWidth: '0 0 1px 0',
                backgroundColor: 'rgba(8, 7, 24, 0.55)',
            }}
        >
            <Box component={RouterLink} to="/" aria-label="CoSparks home" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
                {/* Wordmark collapses to the mark on narrow screens when a page title is showing */}
                <Box sx={{ display: hasTitle ? { xs: 'none', md: 'flex' } : 'flex' }}><Wordmark size={18} /></Box>
                {hasTitle && <Box sx={{ display: { xs: 'flex', md: 'none' } }}><SparkMark size={28} animated /></Box>}
            </Box>
            <WorkspaceNav hasTitle={hasTitle} currentSlug={chrome.workspace?.slug} />

            {hasTitle && (
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: '1px', height: 24, backgroundColor: color.line, display: { xs: 'none', md: 'block' } }} />
                    {chrome.workspace && (
                        // Phones have no room for the kicker line: the workspace is an icon beside the title
                        <IconButton component={RouterLink} to={`/w/${chrome.workspace.slug}`} size="small"
                            aria-label={`In ${chrome.workspace.name} workspace`}
                            sx={{ display: { xs: 'inline-flex', sm: 'none' }, flexShrink: 0, color: color.signal, mr: -0.5 }}>
                            <BusinessOutlined fontSize="small" />
                        </IconButton>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        {(chrome.kicker || chrome.workspace) && (
                            // One line, never broken letter by letter; on phones the title gets the room
                            <Typography noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' }, ...monoLabel(10, '0.14em'), color: color.textFaint, lineHeight: 1.2 }}>
                                {chrome.workspace && (
                                    <>
                                        <Box component={RouterLink} to={`/w/${chrome.workspace.slug}`} aria-label={`In ${chrome.workspace.name} workspace`}
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'bottom', color: color.signal, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, '& svg': { fontSize: 12 } }}>
                                            <BusinessOutlined />{chrome.workspace.name}
                                        </Box>
                                        {chrome.kicker && ' · '}
                                    </>
                                )}
                                {chrome.kicker}
                            </Typography>
                        )}
                        <Typography noWrap sx={{ fontFamily: font.display, fontWeight: 500, fontSize: { xs: 15, sm: 17 }, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
                            {chrome.title}
                        </Typography>
                    </Box>
                </Box>
            )}
            {!hasTitle && <Box sx={{ flex: 1 }} />}

            <SearchTrigger compact={hasTitle} />
            {chrome.actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{chrome.actions}</Box>}
            <AccountMenu />
            {chrome.notice && (
                // Centred below the tools a page keeps in its top corners (a board's tool row and status pill), so it
                // never covers them at any width
                <Box sx={{
                    position: 'absolute', top: TOP_BAR_HEIGHT + 104, left: 0, right: 0, px: 2,
                    display: 'flex', justifyContent: 'center',
                    pointerEvents: 'none', '& > *': { pointerEvents: 'auto', maxWidth: { xs: '100%', sm: 520 } },
                }}>
                    {chrome.notice}
                </Box>
            )}
        </Box>
    );
}

/**
 * Shown while a page's code downloads. Waits 150 ms so fast loads show nothing, then a thin
 * sweeping bar under the top bar: the shell and background stay put.
 */
function RouteLoading() {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 150);
        return () => clearTimeout(t);
    }, []);
    if (!visible) return null;
    return (
        <Box role="progressbar" aria-label="Loading page" sx={{
            position: 'fixed', top: TOP_BAR_HEIGHT, left: 0, right: 0, height: 2, overflow: 'hidden', zIndex: 21,
            '@keyframes cs-route-sweep': { from: { transform: 'translateX(-100%)' }, to: { transform: 'translateX(100%)' } },
            '&::after': {
                content: '""', position: 'absolute', inset: 0,
                background: `linear-gradient(90deg, transparent, ${color.signal}, ${color.spark}, transparent)`,
                animation: `cs-route-sweep 1.1s ${cssEase.out} infinite`,
            },
        }} />
    );
}

/** Full-screen layout around every route: moving background, top bar, page entrances. */
export function AppShell() {
    const location = useLocation();
    const matches = useMatches();
    const immersive = matches.some(m => (m.handle as ShellHandle | undefined)?.immersive);
    const pageKey = pageKeyFor(location.pathname, matches);

    return (
        <ShellChromeProvider>
            <SparkField intensity={immersive ? 'calm' : 'full'} />
            <Box sx={{
                minHeight: '100dvh',
                height: immersive ? '100dvh' : 'auto',
                display: 'flex', flexDirection: 'column',
                transition: `background-color .4s ${cssEase.out}`,
            }}>
                <TopBar />
                <ImpersonationBanner top={TOP_BAR_HEIGHT} />
                <Box component="main" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: immersive ? 'hidden' : 'visible' }}>
                    {/* Suspense here, not only at the app root: loading a page's code keeps the shell on screen */}
                    <Suspense fallback={<RouteLoading />}>
                        <PageFade key={pageKey} routeKey={pageKey}>
                            <Outlet />
                        </PageFade>
                    </Suspense>
                </Box>
            </Box>
            <CookieBanner />
        </ShellChromeProvider>
    );
}
